/**
 * @file 通过chrome wiki调试插件复制组件，支持携带KV数据+跨环境复制随心搭
 */
import {useCallback, useEffect} from 'react';
import * as store from '@/store';
import {
    createBuilder,
    getBuilderEditState, publishArtifact,
    saveBuilderKV,
    updateArtifactCode,
} from '@/kuTools/plugins/flexBuilder/render/request';
import {generateUUID, getTitle} from '@/kuTools/plugins/flexBuilder/utils';
import {flexBuilderInstanceObj} from '@/kuTools/utils';

// 上传kv数据
const uploadKvData = async (builderId: string, kvStorage: any) => {
    const kvPromiseList = [];
    if (Object.keys(kvStorage || {}).length > 0) {
        for (const [key, value] of Object.entries(kvStorage)) {
            kvPromiseList.push(saveBuilderKV(builderId, key, value));
        }
    }
    return Promise.all(kvPromiseList);
};

// 从节点上携带的代码来创建随心搭
const createFBFromCode = async (title: string, builderData: any) => {
    const docGuid = store.getData(store.KEYS.CUR_DOCUMENT)?.docGuid || '';
    const res = await createBuilder(docGuid, undefined, '');
    const builderId = res?.builderId;
    if (!builderId) {
        return;
    }
    const code = builderData.code;
    const builderState = await getBuilderEditState(builderId);
    const artifactId = builderState.previewArtifact?.artifactId;
    const chatId = builderState.chats?.[0]?.chatId;
    const compiledCode = builderData.output?.compiledCode;
    const dependencies = builderData.output?.dependencies;
    const style = builderData.output?.style;
    const artifactIdentify = 'jsx-app-demo';

    if (artifactId && chatId) {
        await updateArtifactCode({
            builderId,
            chatId,
            code,
            artifactId,
            title,
            artifactIdentify,
        });
        await publishArtifact({
            artifactId,
            builderId,
            chatId,
            code,
            compiledCode,
            dependencies,
            style,
            title,
            artifactIdentify,
        });
        return {
            builderId,
            createTime: res.createdAt,
        };
    }
};

// 从一个builderId来创建随心搭
const createFBFromId = async (sourceBuilderId: string) => {
    const docGuid = store.getData(store.KEYS.CUR_DOCUMENT)?.docGuid || '';
    const res = await createBuilder(docGuid, sourceBuilderId, '');
    const builderId = res?.builderId;
    if (!builderId) {
        return;
    }
    return {
        builderId,
        createTime: res.createdAt,
    };
};

export const usePluginCopyEffect = (
    node: any,
    transform: any
) => {
    // 后门数据，节点有该属性时会直接从上面读取KV数据去创建随心搭
    const builderData = node._builderData;
    const sourceHost = node._sourceHost;
    const kvStorage = node._kvStorage;
    const usePluginCopy = !!builderData || !!sourceHost;

    const createFBuilder = useCallback((title: string, builderData: any, sourceBuilderId: string) => {
        if (builderData) {
            return createFBFromCode(title, builderData);
        }
        return createFBFromId(sourceBuilderId);
    }, []);

    useEffect(() => {
        if (!usePluginCopy) {
            return;
        }
        const title = node.title || '未命名';
        createFBuilder(title, builderData, node.builderId)
            .then(async (data) => {
                if (!data?.builderId) {
                    throw new Error('create failed');
                }
                await uploadKvData(data.builderId, kvStorage);
                const {builderId, createTime} = data;
                transform.withoutSaving(() => {
                    transform.commands.setNode(node, {
                        _builderData: null,
                        _sourceHost: null,
                        _kvStorage: null,
                        builderId,
                        isNeedUpload: false,
                        updateKey: generateUUID(8),
                    });
                });
                flexBuilderInstanceObj.set(builderId, {
                    builderId,
                    title,
                    blockId: node.blockId,
                    createTime: createTime,
                });
            })
            .catch(err => {
                transform.withoutSaving(() => {
                    transform.commands.setNode(node, {
                        _builderData: null,
                        _sourceHost: null,
                        _kvStorage: null,
                        updateKey: generateUUID(8),
                    });
                });
                console.error('copy failed：', err);
            });
    }, [builderData, createFBuilder, kvStorage, node, transform, usePluginCopy]);

    return {
        usePluginCopy,
    };
};
